import React, { useState } from "react";
import { dataFileUrl } from '../constant/hostname';
import { ArrowDownTrayIcon, DocumentArrowUpIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';

const Contact: React.FC = () => {
  const filePath = '/app/format/%E6%8A%95%E7%A8%BF%E3%83%95%E3%82%A9%E3%83%BC%E3%83%9E%E3%83%83%E3%83%88.docx';
  const email = 'k4i4t1a1@quartz.ocn.ne.jp';

  const formatHref = dataFileUrl(filePath);
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    const apiUrl = process.env.REACT_APP_CONTACT_API_URL;
    if (!apiUrl) {
      setStatus('error');
      return;
    }
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Network error');
      setStatus('success');
      setFormData({ name: '', email: '', message: '', website: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="w-full flex flex-col items-center py-16 sm:py-24 px-4 sm:px-6 scroll-mt-20 bg-[#f5f0e8]">
      <div className="w-full max-w-5xl">
        {/* セクションヘッダー */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            お問い合わせ
          </h2>
          <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto">
            入会・投稿・その他ご質問など、どんなことでもお気軽にご連絡ください。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-start">
          {/* 左：お問い合わせフォーム */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200/60 shadow-sm">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-5">
              お問い合わせフォーム
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4 relative">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="山田 太郎"
                  className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/40 focus:border-[#4a7c59] transition"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/40 focus:border-[#4a7c59] transition"
                />
              </div>
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="website">ウェブサイト</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="お問い合わせ内容をご記入ください..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/40 focus:border-[#4a7c59] transition"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full flex items-center justify-center gap-2 bg-[#1a4a2e] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#4a7c59] transition disabled:opacity-60"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {status === 'submitting' ? '送信中...' : '送信する'}
              </button>
              {status === 'success' && (
                <p className="text-center text-sm text-[#4a7c59] font-medium">
                  お問い合わせを受け付けました。ありがとうございます。
                </p>
              )}
              {status === 'error' && (
                <p className="text-center text-sm text-red-500">
                  送信に失敗しました。直接メールにてご連絡ください。
                </p>
              )}
            </form>
          </div>

          {/* 右：記事投稿案内 + メール */}
          <div className="space-y-4 sm:space-y-5">
            {/* 記事投稿 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200/60">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#1a4a2e]/10 flex items-center justify-center shrink-0">
                  <DocumentArrowUpIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1a4a2e]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">
                    記事投稿
                  </h4>
                  <p className="text-gray-500 text-xs sm:text-sm mb-2 leading-relaxed">
                    投稿フォーマットをダウンロードし、内容を記載の上、下記アドレスへ添付して送付ください。
                  </p>
                  <a href={`mailto:${email}`} className="text-[#1a4a2e] hover:text-[#4a7c59] underline text-sm break-all transition block mb-4">
                    {email}
                  </a>
                  {formatHref && (
                    <a
                      href={formatHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 border border-[#1a4a2e]/40 text-[#1a4a2e] px-4 py-2 rounded-full text-xs sm:text-sm font-semibold hover:bg-[#1a4a2e]/5 transition"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      投稿フォーマット
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
